import { apiClient } from '@/api/client';
import type { PublicStartupProfile } from '@/features/shared-profile/api/shared-profile.api';
import { filenameFrom, unwrapBlobErrorBody } from '@/lib/utils/download.utils';
import type { SelfIdAnswer } from '@/features/startups/constants';
import type { StartupCompletionField } from '@/lib/types/startup-completion.types';

/** A founding-team member as returned with a startup profile. Email is deliberately excluded - see the implementation plan's Global Constraints (no DB column). */
export interface StartupFoundingTeamMember {
  name: string;
  title: string;
  linkedinUrl?: string | null;
  pastExits?: boolean | null;
  /**
   * No `photoUrl`: the member photo column is dropped Backend-side (MYN-175),
   * so it is neither returned nor accepted. `bio` may still be returned until
   * the Backend drops it, but it was removed from the product (UX sheet r127):
   * never render or send it.
   */
  bio?: string | null;
}

/** A sector row (one primary + optional subsectors) on a startup profile, with both the FK id (Edit form) and resolved name (View). */
export interface StartupProfileSector {
  sectorId: number;
  sector: string;
  subsectorId?: number | null;
  subsector?: string | null;
  isPrimary: boolean;
}

/**
 * Startup entity + profile as returned by `GET /startups/:entityId`
 * (mirrors the backend `StartupProfileResponse`,
 * `Backend/src/modules/startups/startups.service.ts`). Every field nullable/
 * optional except `id`/`entityId`/`companyName`/`hasLeadInvestor` - profiles
 * are creatable with a partial fill (MYN-46).
 */
export interface StartupProfileData {
  id: string;
  entityId: string;
  companyName: string;
  briefDescription?: string | null;
  companyWebsiteUrl?: string | null;
  companyLinkedinUrl?: string | null;
  logoUrl?: string | null;
  fundingStage?: string | null;
  yearFounded?: number | null;
  hqCountryId?: number | null;
  hqStateId?: number | null;
  hqCityId?: number | null;
  hqCity?: string | null;
  hqState?: string | null;
  hqCountry?: string | null;
  /** Read-only business-classification bucket of the HQ country (WORLD_REGIONS label). */
  hqWorldRegion?: string | null;
  /** Free-text sector, set only when the primary sector is "Other". */
  sectorOther?: string | null;
  totalRevenueRange?: string | null;
  arr?: number | null;
  amountRaisedToDate?: number | null;
  nondilutiveFunding?: number | null;
  recentMilestone?: string | null;
  financeSoughtLow?: number | null;
  financeSoughtHigh?: number | null;
  instrument?: string | null;
  targetCloseDate?: string | null;
  valuationOrCap?: number | null;
  hasLeadInvestor: boolean;
  leadInvestorName?: string | null;
  /**
   * The stored S3 KEY despite the name - NOT renderable. Use it only to test
   * whether a deck exists, or to round-trip into the edit form; link to
   * `pitchDeckDownloadUrl` instead.
   */
  pitchDeckUrl?: string | null;
  pitchDeckFileMimeType?: string | null;
  pitchDeckFileSizeBytes?: number | null;
  /** Presigned GET, safe to put in an href. Null when signing failed (MYN-101). */
  pitchDeckDownloadUrl?: string | null;
  /** External link the founder supplied (A-58). Never presigned. */
  pitchVideoUrl?: string | null;
  foundingTeam?: StartupFoundingTeamMember[];
  sectors?: StartupProfileSector[];
  /** The API returns the plain market-model values, not rows (`StartupProfileResponseMapper`). */
  marketModels?: string[];
  investorTypesSought?: string[];
  /** Set by an MNR admin only; read here for the dashboard's data-room card. */
  isSrNextClient?: boolean;
  // Matching fields the create wizard collects (same enum tokens it sends).
  aiClassification?: string | null;
  revenueModel?: string | null;
  revenueGrowthRate?: string | null;
  payingCustomers?: string | null;
  useOfFunds?: string | null;
  employeeCount?: number | null;
  /** Founder self-identification (UX sheet r113/r114). Null = not answered. */
  founderFemale?: SelfIdAnswer | null;
  founderUnderrepresented?: SelfIdAnswer | null;
  founderUnderrepresentedCategories?: string[];
  /** How complete the profile is and what is still empty (MYN-254, Backend `completion`). */
  completion?: StartupProfileCompletion;
}

export type { StartupCompletionField };

export interface StartupProfileCompletion {
  /** Whole-number percentage, 0-100. */
  pct: number;
  missingFields: StartupCompletionField[];
}

/** One founding-team member as sent on PATCH (mirrors `FoundingTeamMemberDto`, Backend). */
export interface StartupProfileUpdateFoundingTeamMember {
  name: string;
  title: string;
  linkedinUrl?: string;
  pastExits?: boolean;
  /** No `photoUrl` or `bio` - see `StartupFoundingTeamMember` above. */
}

/**
 * Body for `PATCH /startups/:entityId` (mirrors the backend
 * `UpdateStartupProfileDto`, `Backend/src/modules/startups/dto/update-startup-profile.dto.ts`).
 * Deliberately NOT `StartupProfileData`: the GET response and the PATCH body
 * have genuinely different shapes for this entity (flat `sectorId`/
 * `subsectorIds` here vs. nested `sectors[]` on read; `pitchDeckFileKey` here
 * vs. `pitchDeckUrl` on read; no `id`/`entityId` - the Backend's
 * `ValidationPipe({ whitelist: true, forbidNonWhitelisted: true })` 400s on
 * any field the DTO doesn't declare). Every field optional (partial update).
 */
export interface StartupProfileUpdatePayload {
  companyName?: string;
  briefDescription?: string;
  companyWebsiteUrl?: string;
  companyLinkedinUrl?: string;
  // No `logoUrl`: the Backend write DTO takes `logoFileKey` (a raw S3 key) and
  // `logoUrl` is now a READ-ONLY resolved presigned URL (MYN-175, A-48/FR-92).
  // Sending it would be rejected outright under `forbidNonWhitelisted`, and the
  // value itself would be a presigned URL written into a key column.
  //
  // `logoFileKey` is the write half, added with the profile's logo upload
  // (MYN-101, `modules/startup/LogoSection.tsx`). Three states, matching the
  // DTO's clear contract: absent = leave the stored logo unchanged, `null` =
  // clear it, a key = store it.
  logoFileKey?: string | null;
  sectorId?: number;
  subsectorIds?: number[];
  fundingStage?: string;
  yearFounded?: number | null;
  hqCountryId?: number;
  /** `null` clears it - sent when the country is no longer the US (r105). */
  hqStateId?: number | null;
  hqCityId?: number | null;
  /** Only while "Other" is the sector; `null` clears a stored value. */
  sectorOther?: string | null;
  totalRevenueRange?: string;
  arr?: number | null;
  amountRaisedToDate?: number | null;
  nondilutiveFunding?: number | null;
  recentMilestone?: string;
  financeSoughtLow?: number | null;
  financeSoughtHigh?: number | null;
  instrument?: string;
  targetCloseDate?: string;
  valuationOrCap?: number | null;
  hasLeadInvestor?: boolean;
  /** `null` clears it - sent when the founder answers "no lead investor". */
  leadInvestorName?: string | null;
  pitchDeckFileKey?: string;
  pitchDeckFileSizeBytes?: number;
  /** External link; empty string clears it (A-58). */
  pitchVideoUrl?: string;
  marketModels?: string[];
  foundingTeam?: StartupProfileUpdateFoundingTeamMember[];
  // Parity with the create wizard (UX sheet r228). `null` / `[]` clears each.
  aiClassification?: string | null;
  revenueModel?: string | null;
  revenueGrowthRate?: string | null;
  payingCustomers?: string | null;
  useOfFunds?: string | null;
  employeeCount?: number | null;
  founderFemale?: SelfIdAnswer | null;
  founderUnderrepresented?: SelfIdAnswer | null;
  founderUnderrepresentedCategories?: string[];
  investorTypesSought?: string[];
}

export async function fetchStartupProfile(entityId: string): Promise<StartupProfileData> {
  const { data } = await apiClient.get<StartupProfileData>(`/startups/${entityId}`);
  return data;
}

/**
 * The redacted profile a share-link recipient sees, for the founder's own
 * "Preview as investor" (feedback call 2026-10-26). Same DTO as the shared
 * view, so the preview can never differ from what is actually shared.
 */
export async function fetchStartupSharePreview(entityId: string): Promise<PublicStartupProfile> {
  const { data } = await apiClient.get<PublicStartupProfile>(`/startups/${entityId}/share-preview`);
  return data;
}

export async function saveStartupProfile(
  entityId: string,
  payload: StartupProfileUpdatePayload,
): Promise<StartupProfileData> {
  const { data } = await apiClient.patch<StartupProfileData>(`/startups/${entityId}`, payload);
  return data;
}

/** A filesystem/URL-safe slug for a company name, for the PDF filename fallback. */
function slugifyCompanyName(companyName: string): string {
  const slug = companyName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'startup';
}

/**
 * Fetch the startup's branded profile PDF (MYN-78) as a blob, with the
 * filename to save it as.
 *
 * Reads the filename from the response's Content-Disposition header; when
 * that header is absent, falls back to a company-name-derived default so the
 * saved file is still identifiable. `responseType: 'blob'` applies to error
 * responses too, so a failure (renderer threw, membership guard rejected)
 * arrives with `response.data` as a Blob containing the structured `{ code,
 * detail }` JSON body rather than the parsed object `getApiErrorMessage`
 * expects - `unwrapBlobErrorBody` (`@/lib/utils/download.utils`) parses it
 * back in place and rethrows the same axios error, mirroring
 * `downloadStartupsExport` (`features/admin/startups/api/startups.api.ts`).
 */
export async function downloadStartupProfilePdf(
  entityId: string,
  companyName: string,
): Promise<{ blob: Blob; filename: string }> {
  const res = await apiClient
    .get(`/startups/${entityId}/profile.pdf`, { responseType: 'blob' })
    .catch(async (error: unknown) => {
      throw await unwrapBlobErrorBody(error);
    });

  const filename = filenameFrom(
    res.headers['content-disposition'],
    `${slugifyCompanyName(companyName)}-profile.pdf`,
  );
  return { blob: res.data as Blob, filename };
}
